import {
  Box,
  Skeleton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import SlideArrows from '@jobseeker/lotus/components/SlideArrows';
import {useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import type {
  AdsElementType,
  AdsEventInfo,
  AppState,
} from '@jobseeker/common-core/types';
import {
  TrackEventUBA,
  parseShowcaseUrl,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {WOMEN_AT_WORK} from '@jobseeker/common-core/constants';

export type PlatinumPartnersProps = {
  label?: string;
  adsEventInfo?: AdsEventInfo;
  slot: string;
};

const PlatinumPartners = ({
  slot,
  label,
  adsEventInfo,
}: PlatinumPartnersProps) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const [platinumAds, setPlatinumAds] = useState<AdsElementType[]>([]);
  const [adsLoaded, setAdsLoaded] = useState(false);

  const shouldLoad = Boolean(slot);
  useLoadAds(
    slot || '',
    setPlatinumAds,
    {},
    false,
    shouldLoad,
    setAdsLoaded,
    shouldLoad
  );

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollDistance = isMobile ? 250 : 370;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollDistance : scrollDistance,
        behavior: 'smooth',
      });
    }
  };

  const trackPlatinumPartnersEvent = (args: {
    type: 'view' | 'click';
    companyId?: string | number;
    companyName?: string;
    custId?: string;
    quoteLineNo?: string;
    centralisedCompanyId?: string;
    items?: AdsElementType[];
  }) => {
    const {
      type,
      companyId,
      companyName,
      custId,
      quoteLineNo,
      centralisedCompanyId,
      items,
    } = args;
    try {
      const refParam =
        new URL(window.location.href).searchParams.get('ref') || '';
      const source = 'Banner';

      if (type === 'view') {
        const list = items || platinumAds;
        const companyIDSArray = list
          .map(ad => ad?.companyId || parseShowcaseUrl(ad?.link))
          .filter(Boolean) as Array<string | number>;
        const companyNamesArray = list
          .map(ad => ad?.title)
          .filter(Boolean) as string[];
        const custIdsArray = list.map(ad => ad?.custId);
        const quoteLineNoArray = list.map(ad => ad?.quoteLineNo);
        const centralCompanyIdsArray = list.map(ad => ad?.centralisedCompanyId);
        const hasQuoteLineNo = quoteLineNoArray.some(line => line != null);
        const hasCustId = custIdsArray.some(line => line != null);
        const hasCentralCompanyId = centralCompanyIdsArray.some(
          line => line != null
        );

        TrackEventUBA('companyBannerView', 'view', {
          ref: refParam,
          source,
          companyIDSArray,
          companyNamesArray,
          ...(hasCustId && {custIdsArray}),
          ...(hasQuoteLineNo && {quoteLineNoArray}),
          ...(hasCentralCompanyId && {centralCompanyIdsArray}),
          page: getUbaPageName(),
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
      } else {
        TrackEventUBA('companyBannerClick', 'click', {
          ref: refParam,
          source,
          companyID: companyId || '',
          companyName: companyName || '',
          ...(custId != null && {custId}),
          ...(quoteLineNo != null && {quoteLineNo}),
          ...(centralisedCompanyId != null && {
            centralCompanyID: centralisedCompanyId,
          }),
          page: getUbaPageName(),
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
      }
    } catch (e) {
      console.error('UBA Event Tracking Error', e);
    }
  };

  useEffect(() => {
    if (!hasTrackedView && platinumAds.length) {
      trackPlatinumPartnersEvent({type: 'view', items: platinumAds});
      setHasTrackedView(true);
    }
  }, [platinumAds, hasTrackedView]);

  if (!platinumAds?.length && adsLoaded) return null;

  return (
    <Box
      position="relative"
      width="100%"
      data-testid={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
      id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '15px',
          mt: '10px',
          mr: isAppIimjobs
            ? '12px'
            : {
                xxs: page === WOMEN_AT_WORK ? '8px' : '24px',
                md: page === WOMEN_AT_WORK ? '0px' : '20px',
              },
        }}
      >
        <Typography
          sx={{
            fontWeight: isAppIimjobs
              ? {xxs: 500, md: 500}
              : {xxs: 600, md: 700},
            mt: 0,
            mb: 0,
          }}
          fontSize={
            isAppIimjobs ? {xxs: '16px', md: '20px'} : {xxs: '20px', md: '22px'}
          }
        >
          {label}
        </Typography>

        <SlideArrows
          scrollLeft={() => scroll('left')}
          scrollRight={() => scroll('right')}
          disableLeft={false}
          disableRight={false}
        />
      </Box>

      <AnimatePresence mode="wait">
        {!adsLoaded ? (
          <motion.div
            key="platinum-skeleton"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                gap: isAppIimjobs ? 2 : 1.25,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                px: 0,
                '&::-webkit-scrollbar': {display: 'none'},
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::after': {
                  content: '""',
                  flex: '0 0 16px',
                },
              }}
            >
              {Array.from({length: 4}).map((_, i) => (
                <Box
                  key={`platinum-skeleton-${i}`}
                  sx={{flex: '0 0 auto', mb: '15px'}}
                >
                  <Skeleton
                    variant="rectangular"
                    width={isMobile ? 270 : 351}
                    height={isMobile ? 90 : 117}
                    sx={{
                      borderRadius: '15px',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </motion.div>
        ) : platinumAds?.length > 0 ? (
          <motion.div
            key="platinum-content"
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 10}}
            transition={{duration: 0.15}}
          >
            <Box
              ref={scrollRef}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                gap: isAppIimjobs ? 2 : 1.25,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                px: 0,
                '&::-webkit-scrollbar': {display: 'none'},
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::after': {
                  content: '""',
                  flex: isAppIimjobs ? '0 0 16px' : '0 0 30px',
                },
              }}
            >
              {platinumAds?.map((adObj: AdsElementType, i: number) => (
                <Box
                  component="a"
                  href={adObj.link || '#'}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  key={`platinum-ad-${i}`}
                  sx={{flex: '0 0 auto', cursor: 'pointer', display: 'block'}}
                  mb={'15px'}
                  onClick={() => {
                    trackPlatinumPartnersEvent({
                      type: 'click',
                      companyId: adObj.companyId,
                      companyName: adObj.title,
                      custId: adObj.custId,
                      quoteLineNo: adObj.quoteLineNo,
                      centralisedCompanyId: adObj.centralisedCompanyId,
                    });
                  }}
                >
                  <Image
                    src={adObj.imageUrl || ''}
                    alt={adObj.title || 'Platinum partner'}
                    width={isMobile ? 270 : 351}
                    height={isMobile ? 90 : 117}
                    style={{
                      objectFit: 'cover',
                      width: isMobile ? '270px' : '351px',
                      height: isMobile ? '90px' : '117px',
                      borderRadius: '15px',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </motion.div>
        ) : (
          <Box sx={{height: 1, mb: 2}} />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default PlatinumPartners;
